// ── Re-exports ──
export * from "./config";
export * from "./domain";
export * from "./exportSchema";
export * from "./noteTiming";
export * from "./timeOfDay";
export * from "./timezones";

import type { TimeOfDayString } from "./timeOfDay";

// ── Tag ──

export interface Tag {
  id: string;
  name: string;
  icon: string; // "phosphor:<name>" or "custom:<data-url>"
  color?: string; // Optional hex color for marker background
  ownerId?: string; // Owner's user id; absent/self means the current user owns it
}

export type CategoryTagSystemKey =
  | "food"
  | "sights"
  | "shopping"
  | "stay"
  | "transport";

export interface CategoryTag {
  id: string;
  name: string;
  systemKey: CategoryTagSystemKey | null;
  sortOrder: number;
  tagIds: string[];
  ownerId?: string;
  ownerName?: string;
}

// ── POI Source ──
// Tracks where a POI recommendation came from (YouTube video, friend, article, etc.)

export interface PoiSource {
  text: string; // Source name/description (e.g., "Mark's Tokyo Tips", "Eater NY")
  url?: string; // Optional link (YouTube video, article URL, etc.)
}

// ── Zoned Time ──
// All schedule times are self-contained RFC 9557 zoned date-time strings,
// e.g. "2026-10-14T10:12+09:00[Asia/Tokyo]" (Temporal.ZonedDateTime serialization).
// The server treats them as opaque validated text; all time logic is client-side.

export type ZonedTimeString = string;

// ── Trip ──

export interface Trip {
  id: string;
  name: string;
  revision: number; // monotonic optimistic-concurrency token
  startDate: string; // YYYY-MM-DD (first trip day)
  endDate: string; // YYYY-MM-DD (last trip day, inclusive)
  defaultTimezone: string; // IANA timezone for days without an override
  timezoneIds: string[]; // ordered optional shortlist (excludes default)
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
  updatedBy: string;
}

// ── Scheduled POI ──

export interface ScheduledPOI {
  id: string;
  poiId: string;
  startTime: ZonedTimeString;
  endTime: ZonedTimeString;
  locked: boolean;
}

// ── Trip Note ──
// Scheduled block on the calendar (transport is a note tagged with a mode).
// A note without timed or all-day dates is an unscheduled inbox note.

export interface TripNote {
  id: string;
  name: string;
  description: string | null;
  startTime: ZonedTimeString | null;
  endTime: ZonedTimeString | null;
  allDayStartDate: string | null; // YYYY-MM-DD; inclusive
  allDayEndDate: string | null; // YYYY-MM-DD; inclusive
  tagIds: string[];
  startPoiId: string | null;
  endPoiId: string | null;
  locked: boolean;
  /**
   * Legs this note belongs to; empty means trip-wide. Unlike POI membership
   * (which lives in trip_leg_pois) this rides inside the full-trip snapshot,
   * because notes are deleted and re-inserted on every autosave.
   */
  legIds: string[];
}

// ── Trip Leg ──
// A named, inclusive date range within a trip. Legs may overlap: a
// hotel-to-hotel handoff day belongs to both.
export interface TripLeg {
  id: string;
  name: string;
  startDate: string; // YYYY-MM-DD; inclusive
  endDate: string; // YYYY-MM-DD; inclusive
  color: number; // 0-7, index into the leg palette
  poiIds: string[];
  noteIds: string[];
}

// ── Accommodation Config ──
// Trip-specific settings for POIs used as accommodations
export interface AccommodationConfig {
  color: number; // 0-7 index for day theming
  checkInTime?: TimeOfDayString; // HH:mm (optional override)
  checkOutTime?: TimeOfDayString; // HH:mm (optional override)
}

// ── Base Location ──
// Shared fields between POI and Accommodation
export interface BaseLocation {
  id: string;
  name: string;
  lat: number | null;
  lng: number | null;
  notes: string | null;
  googleMapsUrl?: string;
}

// ── POI ──

export interface Availability {
  start: string; // ISO 8601
  end: string; // ISO 8601
}

export interface WeatherRequirement {
  condition: string;
  sensitivity: "required" | "desired";
}

export type IdealTimeOfDay = "morning" | "afternoon" | "evening";

export interface POI extends BaseLocation {
  timeBudget: number; // minutes
  tags: string[];
  primaryTagId: string | null; // Tag ID for map icon
  availability: Availability | null;
  idealDays: string[] | null;
  idealTimeOfDay: IdealTimeOfDay | null;
  weatherRequirement: WeatherRequirement | null;
  sources: PoiSource[]; // Where POI recommendations came from
  private?: boolean; // Hidden from trip collaborators' browse-library view
  ownerId?: string; // Owner's user id; absent/self means the current user owns it
  ownerName?: string; // Owner's display name, for foreign POIs
}

// ── Schedule ──
// Day columns derive from the trip's startDate–endDate; a block's day is the
// date of its start in its own zone. Sparse maps are keyed by YYYY-MM-DD date.

export interface Schedule {
  scheduledPOIs: ScheduledPOI[];
  notes: TripNote[];
  accommodationConfigs: Record<string, AccommodationConfig>; // poiId -> trip-specific config
  sleep: Record<string, string>; // date -> poiId (where you sleep that night)
  timezoneOverrides: Record<string, string>; // date -> IANA zone
}

// ── Changelog ──

export type ChangelogAction =
  | "create"
  | "update"
  | "delete"
  | "schedule"
  | "unschedule"
  | "sleep_change"
  | "undo"
  | "redo";

export type EntityType =
  | "poi"
  | "scheduled_poi"
  | "accommodation"
  | "accommodation_config"
  | "note"
  | "trip"
  | "sleep"
  | "timezone"
  | "tag"
  | "category_tag"
  | "collection"
  | "collection_poi"
  | "trip_leg"
  | "trip_leg_poi"
  | "share"
  | "invite";

export type ActivitySource = "ui" | "mcp" | "system";
export type ActivityOutcome = "success" | "failure" | "conflict";
export type ActivityActorType = "user" | "api_token" | "system";

export interface ActivityInverse {
  action: string;
  payload: Record<string, unknown>;
}

export const ACTIVITY_FEED_INVERSE_ACTIONS = [
  "unschedule_poi",
  "restore_scheduled_poi",
  "delete_note",
  "restore_note",
  "update_note",
  "remove_pois_from_trip",
  "add_pois_to_trip",
  "remove_pois_from_collection",
  "add_pois_to_collection",
  "delete_leg",
  "restore_leg",
  "update_leg",
  "remove_pois_from_leg",
  "add_pois_to_leg",
  "clear_sleep",
  "set_sleep",
  "clear_timezone_override",
  "set_timezone_override",
  "clear_accommodation_config",
  "set_accommodation_config",
  "restore_trip_share",
  "restore_collection_share",
  "cancel_trip_invite",
  "restore_trip_invite",
  "cancel_collection_invite",
  "restore_collection_invite",
] as const;

export function isActivityFeedInverseAction(action: string): boolean {
  return (ACTIVITY_FEED_INVERSE_ACTIONS as readonly string[]).includes(action);
}

export interface ChangelogEvent {
  id: string;
  timestamp: string; // ISO 8601
  user: string;
  userId?: string | null; // Clerk user ID (optional for backward compatibility)
  action: ChangelogAction;
  entityType: EntityType;
  entityId: string;
  summary: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  undoOf: string | null;
  source?: ActivitySource;
  actorType?: ActivityActorType;
  actorId?: string | null;
  actorName?: string | null;
  tool?: string | null;
  outcome?: ActivityOutcome;
  undoable?: boolean;
  inverse?: ActivityInverse | null;
  originalActivityId?: string | null;
  redoOf?: string | null;
  conflictReason?: string | null;
}

// ── API Types ──

// TripSummary: lightweight metadata for trip listing
export interface TripSummary {
  id: string;
  name: string;
  startDate: string; // YYYY-MM-DD (first day)
  endDate: string; // YYYY-MM-DD (last day)
  dayCount: number;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
}

// ── Collaboration Types ──

export type PermissionLevel = "viewer" | "editor";

export interface TripShare {
  id: string;
  tripId: string;
  sharedWithUserId: string;
  permissionLevel: PermissionLevel;
  sharedByUserId: string;
  createdAt: string;
}

export interface TripSummaryWithPermission extends TripSummary {
  isOwner: boolean;
  permission: PermissionLevel | "owner";
  sharedWith?: TripShare[]; // Only populated for owners
}

export interface UserProfile {
  id: string;
  email: string;
  displayName?: string;
}

// LibraryData: shared POIs across all trips
// Accommodations are now POIs with "accommodation" tag
export interface LibraryData {
  pois: POI[];
  tags: Tag[];
  categoryTags: CategoryTag[];
}

// TripScheduleData: trip metadata + schedule (no POIs/accommodations)
export interface TripScheduleData {
  trip: Trip;
  schedule: Schedule;
}

export interface CreateTripRequest {
  name: string;
  startDate: string; // YYYY-MM-DD
  dayCount?: number; // defaults to 1
}

// ── Places API Types ──

export interface PlaceResult {
  placeId: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  types: string[];
  googleMapsUrl: string; // constructed from placeId
}

export interface PlacesSearchResponse {
  results: PlaceResult[];
}

// ── Invite Links ──

export type InviteLinkStatus = "pending" | "accepted" | "expired" | "cancelled";

export interface InviteLink {
  id: string;
  tripId: string;
  permissionLevel: PermissionLevel;
  createdByUserId: string;
  token: string;
  status: InviteLinkStatus;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  acceptedByUserId: string | null;
  label: string | null;
}

export interface InviteLinkInfo {
  tripName: string | null;
  permissionLevel: string | null;
  ownerEmail: string | null;
  expiresAt: string | null;
  isValid: boolean;
}

export interface AcceptInviteResult {
  tripId: string;
  permissionLevel: PermissionLevel;
  tripName: string;
}

// ── Collection Types ──
// Collections are user-owned groupings of POIs for organization and sharing

export type CollectionVisibility = "private" | "public" | "shared";

export interface Collection {
  id: string;
  name: string;
  description: string | null;
  visibility: CollectionVisibility;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CollectionWithPois extends Collection {
  poiIds: string[];
  isOwner: boolean;
  permission: PermissionLevel | "owner";
}

// Collection invite links (same pattern as trip invites)
export interface CollectionInviteLink {
  id: string;
  collectionId: string;
  permissionLevel: PermissionLevel;
  createdByUserId: string;
  token: string;
  status: InviteLinkStatus;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  acceptedByUserId: string | null;
  label: string | null;
}

export interface CollectionInviteLinkInfo {
  collectionName: string | null;
  permissionLevel: string | null;
  ownerEmail: string | null;
  expiresAt: string | null;
  isValid: boolean;
}

export interface AcceptCollectionInviteResult {
  collectionId: string;
  permissionLevel: PermissionLevel;
  collectionName: string;
}
